import styles from './style.module.scss'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { TApi, TRoles, TWidgetDomains } from '../../../shared/const'
import http from '../../../shared/http'
import { ReactComponent as ArrowDownIcon } from '../../../assets/icons/arrow-down.svg'
import { types } from '../../../shared/eventEmitter'
import eventEmitter from '../../../shared/eventEmitter'
import { useTranslation } from 'react-i18next'
import { customNumberFormatW, stateFullLabel } from '../../../shared/helpers'
import { widgetHints, widgetLinks } from '../../../shared/widgetListLink'
import InjectRole from '../../../components/HOC/injectRole'

/*
 * Die Kachel der „Allgemeinen Information": ein Bereich, drei Zahlen.
 *
 * Seit 26.08.2026 ist **jede der drei Zahlen ein Verweis** auf genau die Zeilen,
 * die sie zählt (shared/widgetListLink). Vorher war die Kachel ein Bild: Wer die
 * sechs ausstehenden Schüler:innen sehen wollte, ging ins Menü, öffnete die
 * Liste, klappte die Filterleiste auf und stellte den Filter von Hand — drei
 * Handgriffe für eine Zahl, die schon auf dem Bildschirm stand.
 *
 * Zwei Dinge, die dabei leicht schiefgehen:
 *
 * - **Ein Ziel darf fehlen.** Wo es keine Liste gibt, die dieselbe Menge zeigt
 *   (Berichte), bleibt die Zelle eine Zelle. Ein Verweis auf etwas anderes wäre
 *   schlimmer als keiner, weil die Zahl daneben dann falsch aussieht.
 * - **In der Anpassen-Leiste ist die Kachel ein Muster, kein Verweis.** Dort
 *   wählt man aus, was auf der Startseite stehen soll; ein Klick, der
 *   stattdessen die Rechnungsliste öffnet, verlöre die halb fertige Auswahl.
 *   Deshalb `preview` für die Kacheln in der Leiste.
 */

function ThreeSectionsWidget(props) {
	const { t } = useTranslation()
	const [widgetDynamicData, setWidgetDynamicData] = useState({})

	const DEFAULT_OPTION = t('All states')

	const MAP_DOMAIN_TO_DATA = {
		[TWidgetDomains.COMPANIES]: {
			api: TApi.ADMIN_WIDGET_GET_COMPANIES,
			header: t('companies'),
			leftSideText: t('New'),
			leftSideNumberColor: '#3BB055',
			rightSideText: t('Pending'),
			rightSideNumberColor: '#EF8400'
		},
		[TWidgetDomains.STUDENTS]: {
			api: TApi.ADMIN_WIDGET_GET_STUDENTS,
			header: t('students'),
			leftSideText: t('New'),
			leftSideNumberColor: '#3BB055',
			rightSideText: t('Pending'),
			rightSideNumberColor: '#EF8400'
		},
		[TWidgetDomains.REPORTS]: {
			api: TApi.ADMIN_WIDGET_GET_REPORTS,
			header: t('reports'),
			leftSideText: t('New'),
			leftSideNumberColor: '#3BB055',
			rightSideText: t('Overdue'),
			rightSideNumberColor: '#EB5757'
		},
		[TWidgetDomains.INVOICES]: {
			api: TApi.ADMIN_WIDGET_GET_INVOICES,
			header: t('invoices'),
			leftSideText: t('New'),
			leftSideNumberColor: '#3BB055',
			rightSideText: t('Unpaid'),
			rightSideNumberColor: '#EF8400'
		},
		[TWidgetDomains.TEACHERS]: {
			api: TApi.ADMIN_WIDGET_GET_TEACHERS,
			header: t('teachers'),
			leftSideText: t('New'),
			leftSideNumberColor: '#3BB055',
			rightSideText: t('Pending'),
			rightSideNumberColor: '#EF8400'
		}
	}

	const widgetStaticData = MAP_DOMAIN_TO_DATA[props.domain]

	const stateFilter = props.settings?.stateFilter
	const currentOption = stateFilter ? stateFilter : DEFAULT_OPTION

	const links = widgetLinks(props.domain, stateFilter)
	const hints = widgetHints(props.domain, t)

	const fetchWidgetData = () => {
		const params = {
			state: props.settings.stateFilter
		}

		http.get(widgetStaticData.api, { params }).then(({ data }) => {
			setWidgetDynamicData({ ...data.data })
		})
	}

	useEffect(() => {
		fetchWidgetData()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [stateFilter])

	// Das Bundesland der Kachel wählt, wer bundesweit sieht: Bundesadmin und
	// Alumni (#213). Gespeichert wird es an den eigenen Kacheln.
	const mayPickState = props.role === TRoles.FEDERAL_ADMIN || props.role === TRoles.ALUMNI

	const handleOptionsClick = ({ currentOption }) => {
		eventEmitter.emit(
			types.openCustomizeSimpleWidgetModal,
			widgetStaticData.header,
			currentOption,
			props.id,
			props.availableWidgets
		)
	}

	/*
	 * Eine Zelle: Verweis, wenn es ein Ziel gibt — sonst genau dasselbe als
	 * schmuckloses `div`. Nicht zwei Bausteine, sondern einer mit oder ohne
	 * Ziel: Sonst liefen Abstände und Schriftgrade der beiden Fassungen
	 * auseinander, sobald jemand nur eine davon anfasst.
	 */
	const Zelle = ({ to, hint, className, children }) => {
		const gemeinsam = { className: clsx(className, to && styles.verweis), title: hint }

		if (!to || props.preview) return <div {...gemeinsam}>{children}</div>
		return (
			<Link {...gemeinsam} to={to}>
				{children}
			</Link>
		)
	}

	return (
		<div className={styles.root}>
			<Zelle to={links.total} hint={hints.total} className={styles.main}>
				<h1 className={styles.header}>{widgetStaticData.header}</h1>
				{/*
				 * Die Bundesland-Auswahl liegt **im** Verweis auf die Liste und
				 * darf ihn nicht mit auslösen: Wer das Land wechseln will, will
				 * nicht die Liste öffnen.
				 */}
				<div
					onClick={event => {
						if (!mayPickState) return
						event.preventDefault()
						event.stopPropagation()
						handleOptionsClick({ currentOption })
					}}
					className={clsx(styles.options, mayPickState && styles.optionsClickable)}
				>
					<div className={styles.currentOption}>{stateFullLabel(currentOption) || DEFAULT_OPTION}</div>
					{mayPickState ? <ArrowDownIcon /> : ''}
				</div>
				<h2 className={styles.existNumber}>{customNumberFormatW(widgetDynamicData.total)}</h2>
			</Zelle>
			<div className={styles.footerContainer}>
				<Zelle to={links.new} hint={hints.new} className={styles.newContainer}>
					<h2
						className={widgetDynamicData.new ? styles.newNumber : clsx(styles.newNumber, styles.zero)}
						style={{ color: widgetStaticData.leftSideNumberColor }}
					>
						{widgetDynamicData.new ? '+' : ''}
						{customNumberFormatW(widgetDynamicData.new)}
					</h2>
					<p className={styles.statusText}>{widgetStaticData.leftSideText}</p>
				</Zelle>
				<Zelle to={links.pending} hint={hints.pending} className={styles.pendingContainer}>
					<h2 className={styles.pendingNumber} style={{ color: widgetStaticData.rightSideNumberColor }}>
						{customNumberFormatW(widgetDynamicData.pending)}
					</h2>
					<p className={styles.statusText}>{widgetStaticData.rightSideText}</p>
				</Zelle>
			</div>
		</div>
	)
}

ThreeSectionsWidget.propTypes = {
	domain: PropTypes.string.isRequired,
	availableWidgets: PropTypes.array.isRequired,
	id: PropTypes.string.isRequired,
	// Kachel in der Anpassen-Leiste: zeigt dieselben Zahlen, ist aber kein
	// Verweis (siehe Kopfkommentar).
	preview: PropTypes.bool,
	settings: PropTypes.shape({
		schoolFilter: PropTypes.string,
		stateFilter: PropTypes.string
	})
}

export default InjectRole(ThreeSectionsWidget)
